import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, Download, X } from "lucide-react";
import * as React from "react";
import {
  applyFilter,
  findMatches,
  firstError,
  lineFromHash,
  stepMatch,
  type LogFilter,
  type LogLine,
} from "../lib/logs";
import { Button } from "../primitives/button";
import { Input } from "../primitives/input";
import { Callout } from "../primitives/callout";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../primitives/menu";
import { LogLineView, type TimestampMode } from "./log-line";
import { useNow, usePrefs } from "./prefs";

export interface LogViewerProps {
  lines: LogLine[];
  /** The source is still streaming: Follow starts on. */
  live: boolean;
  provenance?: React.ReactNode;
  ranks?: number[];
  indexes?: number[];
  onDownload?: () => void;
  isLoading?: boolean;
  reconnecting?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  /** Values the page also charts (the latest `loss=`) are tinted copper. */
  highlight?: RegExp;
  /** Empty-state phase: "No output yet. Logs appear when the command starts." */
  phase?: string;
}



/** Streaming log viewer (ui-spec §3.4.4, IMP-11): search, Follow, View menu, filters, deep links, virtualized. */
export function LogViewer({ lines, live, provenance, ranks, indexes, onDownload, highlight, phase, isLoading, reconnecting, errorMessage, onRetry }: LogViewerProps) {
  const { timeZone } = usePrefs();
  const now = useNow(10_000);
  const [query, setQuery] = React.useState("");
  const [current, setCurrent] = React.useState(-1);
  const [follow, setFollow] = React.useState(live);
  const [wrap, setWrap] = React.useState(false);
  const [timestamps, setTimestamps] = React.useState<TimestampMode>("absolute");
  const [filter, setFilter] = React.useState<LogFilter>({});
  const [selected, setSelected] = React.useState<number | undefined>(() =>
    typeof location === "undefined" ? undefined : lineFromHash(location.hash),
  );
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const shown = React.useMemo(() => applyFilter(lines, filter), [lines, filter]);
  const matches = React.useMemo(() => findMatches(shown, query), [shown, query]);
  const byLine = React.useMemo(() => {
    const m = new Map<number, typeof matches>();
    for (const x of matches) m.set(x.line, [...(m.get(x.line) ?? []), x]);
    return m;
  }, [matches]);
  const errorLine = React.useMemo(() => firstError(shown), [shown]);
  const lineHeight = 24;

  const virtualizer = useVirtualizer({
    count: shown.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => lineHeight,
    overscan: 30,
  });

  const scrollToLine = React.useCallback(
    (n: number) => {
      const idx = shown.findIndex((l) => l.n === n);
      if (idx >= 0) virtualizer.scrollToIndex(idx, { align: "center" });
    },
    [shown, virtualizer],
  );

  React.useEffect(() => {
    if (follow && shown.length) virtualizer.scrollToIndex(shown.length - 1, { align: "end" });
  }, [follow, shown.length, virtualizer]);

  React.useEffect(() => {
    if (selected !== undefined) scrollToLine(selected);
    // Only the initial deep link scrolls; later selections come from clicks the user can already see.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const m = matches[current];
    if (m) scrollToLine(m.line);
  }, [current, matches, scrollToLine]);



  function onScroll() {
    const el = scrollRef.current;
    if (!el || !follow) return;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - lineHeight * 2) setFollow(false);
  }

  function linkLine(n: number) {
    setSelected(n);
    const url = `${location.pathname}${location.search}#L${n}`;
    history.replaceState(history.state, "", url);
    void navigator.clipboard?.writeText(`${location.origin}${url}`).catch(() => undefined);
  }

  const chips = Object.entries(filter).filter(([, v]) => v !== undefined) as [keyof LogFilter, string | number][];
  const waitingForCommand = !lines.length && !errorMessage && ["Pending", "Queued", "Provisioning", "Starting"].includes(phase ?? "");

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2 pb-2">
        <div className="flex items-center gap-2">
          <Input
            ref={searchRef}
            type="search"
            aria-label="Search logs"
            placeholder="Search logs"
            className="h-[var(--nd-control-height-sm)] w-56"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCurrent(-1);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") setCurrent((c) => stepMatch(c, matches.length, e.shiftKey ? -1 : 1));
            }}
          />
          {query ? (
            <span className="text-small text-fg-muted tabular-nums" aria-live="polite">
              {matches.length ? `${current + 1 > 0 ? current + 1 : 0} of ${matches.length}` : "No matches"}
            </span>
          ) : null}
        </div>
        {chips.map(([k, v]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter((f) => ({ ...f, [k]: undefined }))}
            className="inline-flex h-6 items-center gap-1 rounded-sm border border-line px-2 text-small text-fg-secondary"
          >
            {k} {String(v)} <X className="size-3" aria-label={`Remove ${k} filter`} />
          </button>
        ))}
        {errorLine !== undefined ? (
          <Button variant="ghost" size="sm" className="text-danger-fg" onClick={() => { setSelected(errorLine); scrollToLine(errorLine); setFollow(false); }}>
            First error
          </Button>
        ) : null}
        <div className="ml-auto flex items-center gap-1">
          {provenance ? <span className="mr-2 text-small text-fg-muted">{provenance}</span> : null}
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={follow}
            className="aria-pressed:text-accent-fg"
            onClick={() => setFollow((f) => !f)}
          >
            <span className={follow ? "nd-dot bg-success-dot" : "nd-dot bg-neutral-dot"} aria-hidden />
            Follow
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">View</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onSelect={() => setWrap((w) => !w)}>{wrap ? "Don't wrap lines" : "Wrap lines"}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Timestamps</DropdownMenuLabel>
              {(["absolute", "relative", "off"] as const).map((m) => (
                <DropdownMenuItem key={m} onSelect={() => setTimestamps(m)}>
                  {m === timestamps ? "✓ " : ""}
                  {m[0]!.toUpperCase() + m.slice(1)}
                </DropdownMenuItem>
              ))}
              {lines.some((line) => line.stream) ? <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Stream</DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => setFilter((f) => ({ ...f, stream: "stdout" }))}>stdout only</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setFilter((f) => ({ ...f, stream: "stderr" }))}>stderr only</DropdownMenuItem>
              </> : null}
              {ranks?.length ? <DropdownMenuLabel>Rank</DropdownMenuLabel> : null}
              {ranks?.map((r) => (
                <DropdownMenuItem key={`r${r}`} onSelect={() => setFilter((f) => ({ ...f, rank: r }))}>Rank {r}</DropdownMenuItem>
              ))}
              {indexes?.length ? <DropdownMenuLabel>Index</DropdownMenuLabel> : null}
              {indexes?.map((i) => (
                <DropdownMenuItem key={`i${i}`} onSelect={() => setFilter((f) => ({ ...f, index: i }))}>Index {i}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {onDownload ? (
            <Button variant="ghost" size="sm" disabled={!lines.length || isLoading} onClick={onDownload}>
              <Download aria-hidden /> {isLoading && lines.length ? "Loading logs…" : "Download loaded logs"}
            </Button>
          ) : null}
        </div>
      </div>
      {errorMessage ? (
        <Callout className="mb-3" tone="danger" title="Could not load logs" role="alert" action={onRetry ? <Button size="sm" variant="outline" onClick={onRetry}>Retry logs</Button> : undefined}>
          {errorMessage}{lines.length ? " Previously loaded lines are shown below." : ""}
        </Callout>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2 text-small text-fg-muted" aria-live="polite">
        <span>{reconnecting ? "Reconnecting to logs…" : isLoading ? (lines.length ? "Loading logs…" : "Connecting to logs…") : errorMessage ? "Log connection failed" : live ? "Streaming live" : "Log stream ended"}</span>
        <span>{lines.length.toLocaleString()} lines loaded{chips.length ? ` · ${shown.length.toLocaleString()} shown` : ""}</span>
      </div>
      <div className="relative rounded-lg border border-line bg-canvas">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          tabIndex={0}
          role="log"
          aria-label="Log output"
          className="h-[calc(100vh-232px)] min-h-[360px] overflow-auto py-2"
        >
          {shown.length === 0 ? (
            <p className="p-4 text-ui text-fg-muted">
              {waitingForCommand ? `No output yet. Logs appear when the command starts. · ${phase}` : isLoading ? "Loading logs…" : errorMessage ? "Logs are unavailable. Retry to reconnect." : lines.length ? "No log lines match these filters." : live ? "Waiting for the command to write logs…" : `No logs are available${phase ? ` · ${phase}` : ""}.`}
            </p>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((v) => {
                const line = shown[v.index]!;
                return (
                  <div
                    key={v.key}
                    data-index={v.index}
                    ref={virtualizer.measureElement}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, transform: `translateY(${v.start}px)` }}
                  >
                    <LogLineView
                      line={line}
                      matches={byLine.get(line.n) ?? []}
                      currentMatch={matches[current]}
                      highlight={highlight}
                      timestamps={timestamps}
                      now={now}
                      timeZone={timeZone}
                      selected={selected === line.n}
                      wrap={wrap}
                      lineHref={`#L${line.n}`}
                      onLineLink={linkLine}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {!follow && live ? (
          <button
            type="button"
            onClick={() => setFollow(true)}
            className="absolute right-6 bottom-4 inline-flex items-center gap-1 rounded-full border border-line bg-raised px-3 py-1 text-small text-fg-secondary shadow-popover"
          >
            Jump to latest <ArrowDown className="size-3" aria-hidden />
          </button>
        ) : null}
      </div>
    </div>
  );
}
